import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { THALI, MENU, MENU_NOTES } from '../lib/menu';

const slug = (s) => s.toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const Dining = () => (
  <div className="min-h-screen pt-20">
    <Helmet>
      <title>Menu | Rudragarh Resort, Jaisalmer</title>
      <meta name="description" content="The Rudragarh master menu — Rajasthani specialities, the Shahi Thali, dal baati churma, ker sangri, tandoor breads, pizzas, pastas, South Indian and desserts, fresh from our live open kitchen near Jaisalmer." />
      <meta property="og:title" content="Dining & Menu | Rudragarh Resort" />
      <meta property="og:description" content="Authentic flavours and royal heritage dining at Rudragarh — from the Shahi Thali to desert classics like ker sangri and mirchi ka kutta." />
      <meta property="og:url" content="https://www.rudragarh.com/dining" />
      <meta property="og:image" content="https://www.rudragarh.com/images/resort-cabanas-night.jpg" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Dining & Menu | Rudragarh Resort" />
      <meta name="twitter:description" content="Authentic flavours and royal heritage dining at Rudragarh — from the Shahi Thali to desert classics like ker sangri and mirchi ka kutta." />
      <meta name="twitter:image" content="https://www.rudragarh.com/images/resort-cabanas-night.jpg" />
      <link rel="canonical" href="https://www.rudragarh.com/dining" />
    </Helmet>

    {/* Hero */}
    <section className="relative h-[50vh] flex items-center justify-center" data-testid="dining-hero">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: "url('/images/resort-cabanas-night.jpg')" }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-void/70 via-void/50 to-void" />
      <div className="relative z-10 text-center px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <span className="overline">Dining</span>
          <h1 className="text-4xl md:text-6xl font-heading font-medium mb-4">
            The Master <span className="text-gold-500">Menu</span>
          </h1>
          <p className="text-foreground/70 max-w-xl mx-auto">
            Authentic flavours and royal heritage dining, served fresh from the live open kitchen.
          </p>
        </motion.div>
      </div>
    </section>

    {/* Section jump strip */}
    <section className="sticky top-20 z-40 bg-void/95 backdrop-blur-md border-b border-gold-500/10 py-4">
      <div className="max-w-7xl mx-auto container-padding">
        <div className="flex items-center gap-2 md:gap-4 overflow-x-auto pb-2">
          {MENU.map((s) => (
            <a
              key={s.title}
              href={`#${slug(s.title)}`}
              className="px-4 py-2 text-xs uppercase tracking-widest whitespace-nowrap text-foreground/60 hover:text-gold-500 border border-transparent hover:border-gold-500/30 transition-all"
            >
              {s.title}
            </a>
          ))}
        </div>
      </div>
    </section>

    {/* Signature thali */}
    <section className="pt-16 md:pt-24">
      <div className="max-w-5xl mx-auto container-padding">
        <div className="border border-gold-500/40 bg-void-subtle p-8 md:p-10" data-testid="dining-thali">
          <div className="flex items-baseline justify-between gap-6">
            <h2 className="font-heading text-2xl md:text-3xl text-gold-500 uppercase tracking-[0.15em]">{THALI.name}</h2>
            <span className="font-heading text-2xl text-gold-500 whitespace-nowrap">₹{THALI.price}</span>
          </div>
          <p className="mt-3 text-foreground/60 text-sm italic">{THALI.desc}</p>
        </div>
      </div>
    </section>

    {/* Menu sections */}
    <section className="section-padding pt-16 md:pt-20">
      <div className="max-w-5xl mx-auto container-padding space-y-20">
        {MENU.map((s) => (
          <div key={s.title} id={slug(s.title)} className="scroll-mt-40" data-testid={`menu-${slug(s.title)}`}>
            <h2 className="font-heading text-2xl md:text-3xl text-gold-500 uppercase tracking-[0.2em] pb-3 border-b border-gold-500/20 mb-8">
              {s.title}
            </h2>
            <ul className="grid md:grid-cols-2 gap-x-12 gap-y-5">
              {s.items.map(([name, price, desc]) => (
                <li key={name}>
                  <div className="flex items-baseline gap-3">
                    <span className="font-heading text-lg">{name}</span>
                    <span className="flex-1 border-b border-dotted border-gold-500/20 -translate-y-1" />
                    <span className="font-heading text-lg text-gold-500 whitespace-nowrap">₹{price}</span>
                  </div>
                  {desc && <p className="text-foreground/50 text-sm italic mt-1">{desc}</p>}
                </li>
              ))}
            </ul>
          </div>
        ))}

        <div className="text-center text-foreground/50 text-sm italic space-y-2 pt-8 border-t border-gold-500/20">
          {MENU_NOTES.map((n) => <p key={n}>{n}</p>)}
          <p className="text-gold-500 not-italic pt-4 font-heading text-lg">Thank You For Dining With Us</p>
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="section-padding bg-void-paper" data-testid="dining-cta">
      <div className="max-w-4xl mx-auto text-center container-padding">
        <span className="overline">Come Hungry</span>
        <h2 className="text-3xl md:text-4xl font-heading font-medium mb-6">
          Dinner Under the <span className="text-gold-500">Desert Sky</span>
        </h2>
        <p className="text-foreground/60 mb-8">
          Every dish is prepared fresh in our live open kitchen. Reserve your stay and let us take care of the rest.
        </p>
        <Link to="/book" className="btn-primary">Book Your Stay</Link>
      </div>
    </section>
  </div>
);

export default Dining;
